/**
 * SecuritySummary — the Security tab's Summary sub-view.
 *
 * Composes four EXISTING `/v1/sites/:id/*` endpoints in parallel (no new
 * API surface, per the handoff spec):
 *
 *   GET /v1/sites/:id/threat-senders
 *   GET /v1/sites/:id/blocking
 *   GET /v1/sites/:id/protected-paths
 *   GET /v1/sites/:id/threat-campaigns
 *
 * All four reads are role-gated server-side (the dashboard just renders
 * what the server returns — IPs that the server masked stay masked).
 * One failed fetch must not blank the page: each section renders its own
 * error state, and the others keep showing data.
 *
 * The approved mockup is at
 * `/tmp/.../scratchpad/secmock/project/Summary.dc.html`. The order of the
 * sections below mirrors it:
 *
 *   1. got-through banner (or quiet state)
 *   2. "Are you protected right now" chips (3 chips — Alerts omitted)
 *   3. This-period counters + severity bar
 *   4. WHO + CAMPAIGNS + WHERE FROM
 *   5. WHAT THEY LOOKED FOR + MOST-REQUESTED ATTACK PATHS
 *   6. WHAT TO DO NEXT (≤ 3 ranked actions)
 */

import { useEffect, useMemo, useState } from 'react';

import type { Site } from '@sill/types';
import type {
  BlockingResponse,
  ProtectedPathsResponse,
  ThreatCampaignSummary,
  ThreatCampaignsListResponse,
  ThreatSenderSummary,
  ThreatSendersListResponse,
  ThreatSeverity,
} from '@sill/types/api';

import type { SetView } from '../../app/view-router.js';
import { useOptionalHttp } from '../../api/http-context.js';
import {
  getBlocking,
  getProtectedPaths,
  listThreatCampaigns,
  listThreatSenders,
  putFirewallMode,
  putProtectedPathsMode,
} from '../../api/threats.js';
import { Button, Card, ConfirmDialog } from '../../components/atoms/index.js';
import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { useC } from '../../lib/theme.js';
import type { SecuritySection } from './Security.js';
import { plainClassLine } from './attack-class-plain.js';
import { serverInstallOf, type ServerInstall } from './server-install.js';
import { SHOPIFY_SAFE_WORKER_AVAILABLE } from '../onboarding/install-copy.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

/** Period chip labels + the `date_from` the server receives for each. */
type Period = '24h' | '7d' | '30d' | '90d';
const PERIODS: readonly { id: Period; label: string; words: string }[] = [
  { id: '24h', label: '24h', words: 'the last 24 hours' },
  { id: '7d', label: '7d', words: 'the last 7 days' },
  { id: '30d', label: '30d', words: 'the last 30 days' },
  { id: '90d', label: '90d', words: 'the last 90 days' },
];

function periodDays(p: Period): number {
  return p === '24h' ? 1 : p === '7d' ? 7 : p === '30d' ? 30 : 90;
}

/**
 * Convert a Period into the `date_from` ISO date the API accepts.
 *
 * Founder report 2026-10-20 item 4: the server computes the window as
 * `[midnight(date_from), midnight(date_to)+1day)` when `date_to` is set,
 * or `[midnight(date_from), now)` when it isn't, and bounds the diff at
 * `THREATS_MAX_WINDOW_DAYS` (90). Sending `today - N` for an N-day
 * period makes the diff `> N` days and the API returns 400 for the
 * 90d filter. Send `today - (N-1)` so the window is exactly N days.
 */
function dateFromFor(p: Period): string {
  const now = new Date();
  const d = new Date(now.getTime() - (periodDays(p) - 1) * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

/**
 * The equal-length "previous" window used by the vs-previous trend.
 *
 * Current window is `[today-(N-1) ... today]` → N days exactly. Previous
 * is `[today-(2N-1) ... today-N]` → the previous N-day span, which the
 * server treats as `[midnight(today-(2N-1)), midnight(today-N)+1day)` =
 * N days exactly. Must stay ≤ `THREATS_MAX_WINDOW_DAYS` or the API
 * returns 400 (founder report 2026-10-20 item 4).
 */
function prevWindow(p: Period): { date_from: string; date_to: string } {
  const now = new Date();
  const days = periodDays(p);
  const to = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) };
}

function previousWordsFor(p: Period): string {
  return p === '24h' ? 'previous 24 hours'
    : p === '7d' ? 'previous 7 days'
      : p === '30d' ? 'previous 30 days'
        : 'previous 90 days';
}

/**
 * Short direction word for the trend line — null when ambiguous.
 *
 * Rule for small bases: "up 1100% vs previous 7 days" is silly when
 * the previous period only had 2. When the previous value is < 10 OR
 * either side is 0, say "up from N the previous …" / "down from N
 * the previous …". Only use the percentage when `prev >= 10` and
 * `now > 0`. "same as the previous …" when the two are equal.
 */
export function trendLine(now: number, prev: number | null, periodLabel: string): string | null {
  if (prev === null) return null;
  if (now === 0 && prev === 0) return null;
  if (now === prev) return `same as the ${periodLabel}`;
  const smallBase = prev < 10 || now === 0;
  if (smallBase) {
    return now > prev
      ? `up from ${prev.toLocaleString('en-US')} the ${periodLabel}`
      : `down from ${prev.toLocaleString('en-US')} the ${periodLabel}`;
  }
  const pct = Math.round(((now - prev) / prev) * 100);
  if (pct === 0) return `same as the ${periodLabel}`;
  if (pct > 0) return `up ${pct}% vs ${periodLabel}`;
  return `down ${Math.abs(pct)}% vs ${periodLabel}`;
}

type RemoteState<T> =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; data: T };

export interface SecuritySummaryProps {
  readonly site: Site;
  readonly setView: SetView;
  readonly onSelectSection: (s: SecuritySection) => void;
  /** Open one sender in Attackers. Security owns the tab state, so this
   *  must go through it (a setView to the view already showing does not
   *  switch the tab). */
  readonly onOpenSender: (sender_ref: string) => void;
}

export function SecuritySummary({ site, setView, onSelectSection, onOpenSender }: SecuritySummaryProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [period, setPeriod] = useState<Period>('7d');
  // Founder 2026-10-20: the two switches work from this page; no trip to
  // the Blocking / Attack paths tabs. Bumping `reload` refetches.
  const [reload, setReload] = useState(0);
  const [confirm, setConfirm] = useState<'blocking' | 'paths' | null>(null);
  const [flipping, setFlipping] = useState(false);
  const [flipError, setFlipError] = useState<string | null>(null);

  const [senders, setSenders] = useState<RemoteState<ThreatSendersListResponse>>({ kind: 'loading' });
  const [blocking, setBlocking] = useState<RemoteState<BlockingResponse>>({ kind: 'loading' });
  const [paths, setPaths] = useState<RemoteState<ProtectedPathsResponse>>({ kind: 'loading' });
  const [campaigns, setCampaigns] = useState<RemoteState<ThreatCampaignsListResponse>>({ kind: 'loading' });
  // "vs previous period" trend. The prev window is the same length as
  // the current one and ends where this one begins. Fail soft: if the
  // call errors, we store null and the UI shows nothing.
  const [prevSenders, setPrevSenders] = useState<ThreatSendersListResponse | null>(null);

  // Four reads in parallel. One failure never blanks the page — each
  // section tracks its own error state. `allSettled` is deliberate.
  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    const dateFrom = dateFromFor(period);
    const prev = prevWindow(period);
    setSenders({ kind: 'loading' });
    setBlocking({ kind: 'loading' });
    setPaths({ kind: 'loading' });
    setCampaigns({ kind: 'loading' });
    setPrevSenders(null);
    void Promise.allSettled([
      listThreatSenders(http, site.site_id, { date_from: dateFrom }),
      getBlocking(http, site.site_id),
      getProtectedPaths(http, site.site_id),
      listThreatCampaigns(http, site.site_id, { date_from: dateFrom }),
      // Prev-window senders — fed to the "vs previous N days" line on
      // true full-window tiles. Fails soft (null → no trend line).
      listThreatSenders(http, site.site_id, { date_from: prev.date_from, date_to: prev.date_to }),
    ]).then(([s, b, p, c, prevS]) => {
      if (cancelled) return;
      setSenders(s.status === 'fulfilled' ? { kind: 'loaded', data: s.value } : { kind: 'error' });
      setBlocking(b.status === 'fulfilled' ? { kind: 'loaded', data: b.value } : { kind: 'error' });
      setPaths(p.status === 'fulfilled' ? { kind: 'loaded', data: p.value } : { kind: 'error' });
      setCampaigns(c.status === 'fulfilled' ? { kind: 'loaded', data: c.value } : { kind: 'error' });
      setPrevSenders(prevS.status === 'fulfilled' ? prevS.value : null);
    });
    return () => { cancelled = true; };
  }, [http, site.site_id, period, reload]);

  const periodWords = PERIODS.find((p) => p.id === period)?.words ?? 'this window';
  const sendersData = senders.kind === 'loaded' ? senders.data : null;
  const blockingData = blocking.kind === 'loaded' ? blocking.data : null;
  const pathsData = paths.kind === 'loaded' ? paths.data : null;
  const campaignsData = campaigns.kind === 'loaded' ? campaigns.data : null;
  // Founder 2026-10-29: no Worker or plugin means nothing can count or
  // refuse server requests; Shopify stores cannot add a Worker yet.
  const serverInstall: ServerInstall = serverInstallOf(site, {
    install_version: pathsData?.install_version ?? null,
    install_source: blockingData?.blocking.install_source ?? null,
  });
  const workerOffered = serverInstall !== 'shopify_none' || SHOPIFY_SAFE_WORKER_AVAILABLE;

  // Per the handoff spec: "any_succeeded" is PER SENDER. The banner MUST
  // say how many SENDERS got a success response, never a request count.
  //
  // Founder report 2026-10-20 item 5: handled senders (blocked / allowed
  // / ignored) sort LAST, so a succeeded sender can be off the first
  // page. Trust the server's full-window `senders_got_through` for the
  // banner / tiles; use the loaded-page `.filter(any_succeeded)` only to
  // find the FIRST succeeded sender to deep-link to.
  const senderList = sendersData?.senders ?? [];
  const succeededSenders = useMemo(
    () => senderList.filter((s) => s.any_succeeded),
    [senderList],
  );
  const sendersGotThrough = typeof sendersData?.senders_got_through === 'number'
    ? sendersData.senders_got_through
    : succeededSenders.length;
  const anySucceeded = sendersGotThrough > 0;

  // Severity counts for the bar (sum must equal the sender total count).
  const severityCounts: Record<ThreatSeverity, number> = sendersData?.counts_by_severity
    ?? { critical: 0, high: 0, medium: 0, low: 0 };

  // What they looked for — aggregated from the senders on the page.
  // Honestly labelled: "across the senders shown below" (not total).
  const classAgg = useMemo((): ReadonlyArray<{ label: string; count: number }> => {
    const map = new Map<string, number>();
    for (const s of senderList) {
      for (const label of s.class_labels) {
        map.set(label, (map.get(label) ?? 0) + 1);
      }
    }
    return [...map.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [senderList]);

  // Where from — ASN / country, aggregated from the same senders.
  const asnAgg = useMemo((): ReadonlyArray<{ org: string; share: number }> => {
    const map = new Map<string, number>();
    for (const s of senderList) {
      const org = s.as_org ?? 'Unknown network';
      map.set(org, (map.get(org) ?? 0) + 1);
    }
    const total = senderList.length;
    if (total === 0) return [];
    return [...map.entries()]
      .map(([org, n]) => ({ org, share: n / total }))
      .sort((a, b) => b.share - a.share)
      .slice(0, 4);
  }, [senderList]);

  const countryAgg = useMemo((): readonly string[] => {
    const set = new Set<string>();
    for (const s of senderList) {
      if (s.country !== null) set.add(s.country);
    }
    return [...set].slice(0, 5);
  }, [senderList]);

  const topCampaigns = campaignsData?.campaigns.slice(0, 3) ?? [];
  const topSenders = senderList.slice(0, 5);

  // Fleet signal — how many of the loaded senders also probed other
  // Sill-protected sites in the trailing 24 h. Count only; never names.
  // Hidden when zero or when nothing attested (prevents a soft 0 line).
  const fleetCount = useMemo(() => {
    let n = 0;
    for (const s of senderList) {
      const v = s.elsewhere_sites_24h;
      if (typeof v === 'number' && v > 0) n += 1;
    }
    return n;
  }, [senderList]);

  // Ranked What-to-do-next actions. The rules come from the handoff spec:
  // (1) succeeded sender + not blocked → Block it
  // (2) blocking off OR install not ready → Turn on blocking / update Worker
  // (3) attack paths observe + would_refuse > 0 + can enforce → Turn on paths
  // (4) campaign with any_succeeded + no auto-block → Review campaign
  // (5) nothing → "Nothing to do right now."
  const flip = async (which: 'blocking' | 'paths'): Promise<void> => {
    if (http === null) return;
    setFlipping(true);
    setFlipError(null);
    try {
      const r = which === 'blocking'
        ? await putFirewallMode(http, site.site_id, 'enforce')
        : await putProtectedPathsMode(http, site.site_id, 'enforce');
      if (!r.ok) {
        setFlipError(r.error === 'install_cannot_enforce'
          ? 'Your Cloudflare Worker is too old to refuse requests. Update it in Settings › Install first.'
          : 'That change was rejected. Try again, or use the Blocking tab.');
      }
    } catch {
      setFlipError('Something went wrong. Try again.');
    } finally {
      setFlipping(false);
      setConfirm(null);
      setReload((n) => n + 1);
    }
  };

  const nextActions = useMemo((): ReadonlyArray<{
    key: string;
    title: string;
    hint: string;
    onClick: () => void;
    primary: boolean;
    cta: string;
  }> => {
    const out: Array<{ key: string; title: string; hint: string; onClick: () => void; primary: boolean; cta: string }> = [];

    const openSender = succeededSenders.find((s) => s.state === 'none');
    if (openSender !== undefined) {
      out.push({
        key: 'block-succeeded',
        title: `Block ${openSender.sender_ip_display}`,
        hint: 'The sender whose request got through. Open it to block it, then check that path with your host.',
        onClick: () => {
          onOpenSender(openSender.sender_ref);
        },
        primary: true,
        cta: 'Open this sender',
      });
    }

    if (blockingData !== null) {
      const b = blockingData.blocking;
      if (b.mode === 'off' && b.install === 'worker_ready') {
        out.push({
          key: 'turn-on-blocking',
          title: 'Turn on blocking',
          hint: 'Addresses you’ve blocked are not being refused yet.',
          onClick: () => (blockingData.may_mutate ? setConfirm('blocking') : onSelectSection('blocking')),
          primary: out.length === 0,
          cta: blockingData.may_mutate ? 'Turn on' : 'Open blocking',
        });
      } else if (b.install === 'worker_outdated') {
        out.push({
          key: 'update-worker',
          title: 'Update the Cloudflare Worker',
          hint: 'Your Worker is below the version that can refuse requests.',
          onClick: () => setView('settings', { settings_tab: 'install' }),
          primary: out.length === 0,
          cta: 'Open install',
        });
      } else if (b.install === 'not_in_path' && workerOffered) {
        out.push({
          key: 'set-up-worker',
          title: 'Set up the Cloudflare Worker',
          hint: 'Blocking needs the Worker in the request path.',
          onClick: () => setView('settings', { settings_tab: 'install' }),
          primary: out.length === 0,
          cta: 'Open install',
        });
      }
    }

    if (
      pathsData !== null
      && pathsData.mode === 'observe'
      && pathsData.would_refuse_count_window > 0
      && pathsData.install_can_enforce
      && serverInstall === 'server'
    ) {
      out.push({
        key: 'turn-on-paths',
        title: 'Turn on attack paths',
        hint: `${pathsData.would_refuse_count_window.toLocaleString('en-US')} requests for secret files hit your site in this window.`,
        onClick: () => (pathsData.may_mutate ? setConfirm('paths') : onSelectSection('paths')),
        primary: out.length === 0,
        cta: pathsData.may_mutate ? 'Turn on' : 'Open attack paths',
      });
    }

    const campaignToReview = topCampaigns.find((c) => c.any_succeeded);
    if (campaignToReview !== undefined && out.length < 3) {
      out.push({
        key: 'review-campaign',
        title: `Review ${campaignToReview.sender_count.toLocaleString('en-US')} addresses on ${campaignToReview.as_org ?? 'one network'}`,
        hint: campaignToReview.headline,
        onClick: () => onSelectSection('attackers'),
        primary: out.length === 0,
        cta: 'Review',
      });
    }

    return out.slice(0, 3);
  }, [succeededSenders, blockingData, pathsData, topCampaigns, setView, onSelectSection, serverInstall, workerOffered]);

  const totalSenders = sendersData?.total_count ?? senderList.length;
  const totalCampaigns = campaignsData?.campaigns.length ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="security-summary">
      {flipError !== null ? (
        <div role="alert" data-testid="security-flip-error" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{flipError}</div>
      ) : null}
      <ConfirmDialog
        open={confirm === 'blocking'}
        onClose={() => setConfirm(null)}
        title={`Turn on blocking for ${site.domain}?`}
        confirmLabel={flipping ? 'Turning on…' : 'Turn on blocking'}
        cancelLabel="Not now"
        confirmTone="lime"
        onConfirm={() => { void flip('blocking'); }}
        body={(
          <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: 0 }}>
            Addresses you block will get “Access denied” from your Cloudflare Worker, starting with the {(blockingData?.counts.live_blocks ?? 0).toLocaleString('en-US')} already saved. You can turn it off again with one click.
          </p>
        )}
      />
      <ConfirmDialog
        open={confirm === 'paths'}
        onClose={() => setConfirm(null)}
        title={`Block attack paths on ${site.domain}?`}
        confirmLabel={flipping ? 'Turning on…' : 'Turn on'}
        cancelLabel="Not now"
        confirmTone="lime"
        onConfirm={() => { void flip('paths'); }}
        body={(
          <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: 0 }}>
            Requests for secret files and other attack paths will get a plain “not found” instead of reaching your server. In the last {pathsData?.window_days ?? 7} days this would have stopped {(pathsData?.would_refuse_count_window ?? 0).toLocaleString('en-US')} requests. Normal pages are not affected.
          </p>
        )}
      />
      {/* Period selector: the same segmented control as Overview's range
          picker (Dashboard.tsx), so filters look alike across pages. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div
          className="flex items-center"
          role="group"
          aria-label="Period"
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          {PERIODS.map((p, i) => {
            const sel = period === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                data-testid={`security-period-${p.id}`}
                aria-pressed={sel}
                className="px-2.5 py-1.5"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  letterSpacing: 1,
                  fontWeight: 500,
                  color: sel ? C.text : C.muted,
                  background: sel ? C.surface2 : 'transparent',
                  borderRight: i < PERIODS.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Section 1 — got-through banner, or the quiet state. */}
      <GotThroughBanner
        anySucceeded={anySucceeded}
        succeededCount={sendersGotThrough}
        periodWords={periodWords}
        onOpenAttackers={() => {
          const target = succeededSenders[0];
          if (target !== undefined) {
            onOpenSender(target.sender_ref);
          } else {
            onSelectSection('attackers');
          }
        }}
        error={senders.kind === 'error'}
        // Founder 2026-10-20: hold the "nothing got through" quiet copy
        // until the first fetch settles. The old render flashed the
        // mint strip for ~1 round-trip before data arrived.
        loading={senders.kind === 'loading'}
      />

      {/* Section 2 — Are you protected right now? */}
      <ProtectedChips
        blocking={blocking}
        paths={paths}
        onOpenBlocking={() => onSelectSection('blocking')}
        onOpenPaths={() => onSelectSection('paths')}
        onOpenInstall={() => setView('settings', { settings_tab: 'install' })}
        serverInstall={serverInstall}
      />

      {/* Section 3 — This period counters + severity bar. */}
      <ThisPeriodBlock
        senders={senders}
        blocking={blocking}
        totalCampaigns={totalCampaigns}
        severityCounts={severityCounts}
        prevSenders={prevSenders}
        previousWords={previousWordsFor(period)}
        onOpenAttackers={() => onSelectSection('attackers')}
      />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* Section 4a — WHO. */}
        <WhoCard
          state={senders}
          topSenders={topSenders}
          totalSenders={totalSenders}
          fleetCount={fleetCount}
          onOpenSender={(s) => onOpenSender(s.sender_ref)}
          onOpenAll={() => onSelectSection('attackers')}
        />
        {/* Section 4b — CAMPAIGNS + WHERE FROM. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <CampaignsCard
            state={campaigns}
            top={topCampaigns}
            onOpenAll={() => onSelectSection('attackers')}
          />
          <WhereFromCard
            senderCount={senderList.length}
            totalCount={totalSenders}
            asnAgg={asnAgg}
            countries={countryAgg}
          />
        </div>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* Section 5a — WHAT THEY LOOKED FOR. */}
        <WhatCard
          state={senders}
          classAgg={classAgg}
          senderCount={senderList.length}
          totalCount={totalSenders}
        />
        {/* Section 5b — Most-requested attack paths. */}
        <TopPathsCard state={paths} onShowAll={() => onSelectSection('paths')} />
      </div>

      {/* Section 6 — WHAT TO DO NEXT. */}
      <NextActions actions={nextActions} loading={senders.kind === 'loading' && blocking.kind === 'loading'} />
    </div>
  );
}

/* ------------------------------------------------------------------- *
 *  Section components (kept in the same file; each is small and only  *
 *  rendered from this page).                                          *
 * ------------------------------------------------------------------- */

function GotThroughBanner({
  anySucceeded,
  succeededCount,
  periodWords,
  onOpenAttackers,
  error,
  loading,
}: {
  readonly anySucceeded: boolean;
  readonly succeededCount: number;
  readonly periodWords: string;
  readonly onOpenAttackers: () => void;
  readonly error: boolean;
  /** First-fetch sentinel. While `true` we never render the quiet
   *  "nothing got through" strip — see Section 1 docblock. */
  readonly loading: boolean;
}): JSX.Element | null {
  const C = useC();
  if (loading) {
    // Hold the row's height with a monochrome skeleton rather than
    // the mint "all clear" strip. Dimensions match the strip so there
    // is no layout shift on hand-off.
    return (
      <div
        data-testid="security-summary-banner-loading"
        aria-busy="true"
        aria-label="Loading recent attack senders"
        style={{
          padding: '14px 18px',
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          minHeight: 50,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 160,
            height: 10,
            background: C.surface2,
            borderRadius: 2,
          }}
        />
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="security-summary-attackers-error"
        style={{
          padding: 14,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
        }}
      >
        Couldn’t load the attacker list. We’ll try again in a moment.
      </div>
    );
  }
  if (anySucceeded) {
    // Founder review 2026-10-20: the old copy ("got a success response")
    // read as "the attack worked". A 200 on `/.env` can just be your
    // normal catch-all page returning 200 — the merchant needs to
    // verify. Reword so the banner says what we know (a 200 on a
    // sensitive path) and asks the merchant to check which file was
    // served. Keep the existing hint about contacting the host.
    const label = succeededCount === 1
      ? '1 sender got a 200 on a sensitive path'
      : `${succeededCount.toLocaleString('en-US')} senders got a 200 on a sensitive path`;
    return (
      <div
        data-testid="security-summary-banner-attacked"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '16px 18px',
          background: C.coralDim,
          border: `1px solid ${C.coralFg}`,
          borderRadius: 3,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 420px' }}>
          <div style={{ fontFamily: sansFont, fontWeight: 600, fontSize: 15, color: C.coralFg }}>
            {label}
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.5, marginTop: 3 }}>
            Check whether that was the real file or your normal not-found page. Open the sender for the request details, then check that path with your host.
          </div>
        </div>
        <Button type="button" variant="primary" onClick={onOpenAttackers}>
          Open this sender
        </Button>
      </div>
    );
  }
  return (
    <div
      data-testid="security-summary-banner-quiet"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '14px 18px',
        background: C.mintDim,
        border: `1px solid ${C.mintFg}`,
        borderRadius: 3,
      }}
    >
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={C.mintFg} strokeWidth={2} aria-hidden="true">
        <path d="M5 12l5 5L20 7" />
      </svg>
      <div style={{ fontFamily: sansFont, fontSize: 14, color: C.mintFg, fontWeight: 500 }}>
        No attack request got through in {periodWords}.
      </div>
    </div>
  );
}

function ProtectedChips({
  blocking,
  paths,
  onOpenBlocking,
  onOpenPaths,
  onOpenInstall,
  serverInstall = 'server',
}: {
  readonly blocking: RemoteState<BlockingResponse>;
  readonly paths: RemoteState<ProtectedPathsResponse>;
  readonly onOpenBlocking: () => void;
  readonly onOpenPaths: () => void;
  readonly onOpenInstall: () => void;
  readonly serverInstall?: ServerInstall;
}): JSX.Element {
  const C = useC();
  const blockingLabel = (() => {
    if (blocking.kind === 'loading') return { dot: C.muted, title: 'Loading…', sub: '' };
    if (blocking.kind === 'error') return { dot: C.coralFg, title: 'Could not load', sub: 'Try again in a moment' };
    const b = blocking.data;
    const saved = b.counts.live_blocks;
    if (serverInstall === 'shopify_none' && b.blocking.mode !== 'on') {
      return { dot: C.muted, title: 'Not available yet', sub: 'Needs the Worker in front of your store' };
    }
    // Install can enforce only when the Worker is in the path AND
    // on the enforcing version. If not, the dot is amber even when
    // mode is on — nothing is actually being refused.
    const installReady = b.blocking.install === 'worker_ready';
    if (b.blocking.mode === 'on' && installReady) {
      return {
        dot: C.mintFg,
        title: `On · ${b.blocking.delivery === 'live' ? 'live' : b.blocking.delivery ?? 'pending'}`,
        sub: `${saved.toLocaleString('en-US')} addresses blocked`,
      };
    }
    if (b.blocking.mode === 'on' && !installReady) {
      return {
        dot: C.amberFg,
        title: 'On · not in path',
        sub: saved > 0
          ? `${saved.toLocaleString('en-US')} saved, not enforced`
          : 'Blocking cannot run on this install',
      };
    }
    return {
      dot: C.amberFg,
      title: 'Off',
      sub: saved > 0
        ? `${saved.toLocaleString('en-US')} saved, not enforced`
        : 'Nothing blocked yet',
    };
  })();

  const installLabel = (() => {
    if (blocking.kind !== 'loaded') return { dot: C.muted, title: '—', sub: '' };
    const install = blocking.data.blocking.install;
    if (serverInstall === 'shopify_none' && install === 'not_in_path') {
      return SHOPIFY_SAFE_WORKER_AVAILABLE
        ? { dot: C.muted, title: 'Not set up', sub: 'Optional: put it in front of your store' }
        : { dot: C.muted, title: 'Coming soon', sub: 'For Shopify stores on Cloudflare' };
    }
    if (install === 'worker_ready') return { dot: C.mintFg, title: 'Up to date', sub: 'Cloudflare Worker in place' };
    if (install === 'worker_outdated') return { dot: C.amberFg, title: 'Update available', sub: 'Open Install to update the Worker' };
    return { dot: C.coralFg, title: 'Not in the path', sub: 'Open Install to set up the Worker' };
  })();

  const pathsLabel = (() => {
    if (paths.kind === 'loading') return { dot: C.muted, title: 'Loading…', sub: '' };
    if (paths.kind === 'error') return { dot: C.coralFg, title: 'Could not load', sub: 'Try again in a moment' };
    const p = paths.data;
    if (p.mode !== 'enforce' && serverInstall === 'shopify_none') {
      return { dot: C.muted, title: 'Handled by Shopify', sub: 'These requests never reach a server of yours' };
    }
    if (p.mode !== 'enforce' && serverInstall === 'script_none') {
      return { dot: C.muted, title: 'Not measured', sub: 'Needs the Worker or WordPress plugin' };
    }
    if (p.mode === 'enforce') {
      return {
        dot: C.mintFg,
        title: 'Refusing',
        sub: `${p.refused_count_window.toLocaleString('en-US')} refused in the window`,
      };
    }
    return {
      dot: C.amberFg,
      title: 'Watching only',
      sub: `${p.would_refuse_count_window.toLocaleString('en-US')} would have been refused`,
    };
  })();

  const chip = (
    kicker: string,
    title: string,
    sub: string,
    dot: string,
    onClick: () => void,
    testid: string,
  ): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        textAlign: 'left',
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        cursor: 'pointer',
      }}
    >
      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>{kicker}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontWeight: 600, fontSize: 14, color: C.text }}>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: dot }} />
        {title}
      </div>
      {sub.length > 0 ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>{sub}</div> : null}
    </button>
  );

  return (
    <section aria-label="Are you protected right now" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
        Are you protected right now
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10,
        }}
      >
        {chip('Blocking', blockingLabel.title, blockingLabel.sub, blockingLabel.dot, onOpenBlocking, 'protected-chip-blocking')}
        {chip('Cloudflare Worker', installLabel.title, installLabel.sub, installLabel.dot, onOpenInstall, 'protected-chip-worker')}
        {chip('Attack paths', pathsLabel.title, pathsLabel.sub, pathsLabel.dot, onOpenPaths, 'protected-chip-paths')}
      </div>
    </section>
  );
}

function ThisPeriodBlock({
  senders,
  blocking,
  totalCampaigns,
  severityCounts,
  prevSenders,
  previousWords,
  onOpenAttackers,
}: {
  readonly senders: RemoteState<ThreatSendersListResponse>;
  readonly blocking: RemoteState<BlockingResponse>;
  readonly totalCampaigns: number;
  readonly severityCounts: Record<ThreatSeverity, number>;
  readonly prevSenders: ThreatSendersListResponse | null;
  readonly previousWords: string;
  readonly onOpenAttackers: () => void;
}): JSX.Element {
  const C = useC();
  const sendersData = senders.kind === 'loaded' ? senders.data : null;
  const blockingData = blocking.kind === 'loaded' ? blocking.data : null;

  // Founder report 2026-10-20 item 5: the attack-requests tile and the
  // got-through tiles must read the FULL window, not just the loaded
  // page. The server now returns `requests_total` + `senders_got_through`
  // (independent of pagination). Fall back to the page sum for old
  // responses so the tile isn't blank during a staged rollout.
  const requestsTotal = typeof sendersData?.requests_total === 'number'
    ? sendersData.requests_total
    : (sendersData?.senders ?? []).reduce((n, s) => n + s.request_count, 0);
  const totalSenders = sendersData?.total_count ?? 0;
  const totalBlocked = blockingData?.counts.live_blocks ?? 0;
  const criticalHigh = severityCounts.critical + severityCounts.high;
  const prevTotalSenders = typeof prevSenders?.total_count === 'number' ? prevSenders.total_count : null;
  const prevCounts = prevSenders?.counts_by_severity;
  const prevCriticalHigh = prevCounts !== undefined
    ? (prevCounts.critical ?? 0) + (prevCounts.high ?? 0)
    : null;

  const sevColor: Record<ThreatSeverity, string> = {
    critical: C.coralFg, high: C.amberFg, medium: C.skyFg, low: C.dim,
  };
  const sevTotal = severityCounts.critical + severityCounts.high + severityCounts.medium + severityCounts.low;

  const tile = (
    big: string,
    sub: string,
    onClick?: () => void,
    testid?: string,
    trend?: string | null,
  ): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        textAlign: 'left',
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 14,
        cursor: onClick ? 'pointer' : 'default',
      }}
      disabled={onClick === undefined}
    >
      <div style={{ fontFamily: serifFont, fontSize: 34, lineHeight: 1, color: C.text }}>{big}</div>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 6 }}>{sub}</div>
      {typeof trend === 'string' && trend.length > 0 ? (
        <div
          data-testid={testid !== undefined ? `${testid}-trend` : undefined}
          style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 4 }}
        >
          {trend}
        </div>
      ) : null}
    </button>
  );

  return (
    <section aria-label="This period" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
        This period
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
        }}
      >
        {tile(requestsTotal.toLocaleString('en-US'), 'attack requests from these senders', onOpenAttackers, 'tile-requests')}
        {tile(
          totalSenders.toLocaleString('en-US'),
          `senders · ${totalCampaigns.toLocaleString('en-US')} ${totalCampaigns === 1 ? 'campaign' : 'campaigns'}`,
          onOpenAttackers,
          'tile-senders',
          trendLine(totalSenders, prevTotalSenders, previousWords),
        )}
        {tile(
          criticalHigh.toLocaleString('en-US'),
          criticalHigh === 1 ? 'critical or high sender' : 'critical or high senders',
          onOpenAttackers,
          'tile-crit-high',
          trendLine(criticalHigh, prevCriticalHigh, previousWords),
        )}
        {tile(totalBlocked.toLocaleString('en-US'), 'addresses blocked', undefined, 'tile-blocked')}
      </div>
      {sevTotal > 0 ? (
        <>
          <div
            role="img"
            aria-label={`By severity: ${severityCounts.critical} critical, ${severityCounts.high} high, ${severityCounts.medium} medium, ${severityCounts.low} low senders`}
            style={{ display: 'flex', height: 10, borderRadius: 2, overflow: 'hidden', marginTop: 8 }}
          >
            {(['critical', 'high', 'medium', 'low'] as const).map((k) =>
              severityCounts[k] > 0 ? (
                <div
                  key={k}
                  aria-label={`${severityCounts[k]} ${k} senders`}
                  style={{ flex: severityCounts[k], background: sevColor[k] }}
                />
              ) : null,
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, fontFamily: sansFont, fontSize: 12, color: C.body, flexWrap: 'wrap' }}>
            {(['critical', 'high', 'medium', 'low'] as const).map((k) => (
              <span key={k}>{severityCounts[k]} {k}</span>
            ))}
            <span style={{ color: C.muted }}>· severity is what they tried, not whether it worked</span>
          </div>
        </>
      ) : null}
    </section>
  );
}

function WhoCard({
  state,
  topSenders,
  totalSenders,
  fleetCount,
  onOpenSender,
  onOpenAll,
}: {
  readonly state: RemoteState<ThreatSendersListResponse>;
  readonly topSenders: readonly ThreatSenderSummary[];
  readonly totalSenders: number;
  readonly fleetCount: number;
  readonly onOpenSender: (s: ThreatSenderSummary) => void;
  readonly onOpenAll: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
          Who · top senders
        </div>
        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Couldn’t load senders. Try again in a moment.
          </div>
        ) : topSenders.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Nothing to show for this period.
          </div>
        ) : (
          <>
            {topSenders.map((s) => {
              const seenBefore = typeof s.prior_alerts_30d === 'number' && s.prior_alerts_30d > 0
                ? s.prior_alerts_30d : null;
              const elsewhere = typeof s.elsewhere_sites_24h === 'number' && s.elsewhere_sites_24h > 0
                ? s.elsewhere_sites_24h : null;
              return (
                <div
                  key={s.sender_ref}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '26px minmax(0, 1fr) auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  <CountryFlag code={s.country} size={16} />
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>{s.sender_ip_display}</div>
                    <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                      {s.as_org ?? 'Unknown network'} · {s.class_summary}
                    </div>
                    {seenBefore !== null || elsewhere !== null ? (
                      <div
                        style={{
                          display: 'flex', flexWrap: 'wrap', gap: 4,
                          marginTop: 2,
                        }}
                      >
                        {seenBefore !== null ? (
                          <SeenBeforeChip
                            testid={`summary-seen-before-${s.sender_ref}`}
                            count={seenBefore}
                          />
                        ) : null}
                        {elsewhere !== null ? (
                          <FleetChip
                            testid={`summary-elsewhere-${s.sender_ref}`}
                            count={elsewhere}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenSender(s)}
                    data-testid={`summary-sender-${s.sender_ref}`}
                    style={{
                      font: `500 12px ${sansFont}`,
                      padding: '5px 10px',
                      background: s.state === 'blocked' ? C.surface2 : C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      color: C.text,
                      cursor: 'pointer',
                    }}
                  >
                    {s.state === 'blocked' ? 'Blocked' : s.state === 'trusted' ? 'Trusted' : 'Open'}
                  </button>
                </div>
              );
            })}
            {fleetCount > 0 ? (
              <div
                data-testid="summary-fleet-line"
                style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
              >
                {fleetCount.toLocaleString('en-US')} of these senders also probed other Sill-protected sites in the last 24 hours.
              </div>
            ) : null}
            <button
              type="button"
              onClick={onOpenAll}
              style={{
                font: `400 13px ${sansFont}`,
                padding: '4px 0',
                color: C.limeFg,
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              All {totalSenders.toLocaleString('en-US')} senders in Attackers →
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

/** Muted chip: "seen before · N in 30 days". */
function SeenBeforeChip({ count, testid }: { readonly count: number; readonly testid: string }): JSX.Element {
  const C = useC();
  return (
    <span
      data-testid={testid}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 6px',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1,
        color: C.muted,
      }}
    >
      seen before · {count.toLocaleString('en-US')} in 30 days
    </span>
  );
}

/** Muted chip: "also on N other Sill sites". */
function FleetChip({ count, testid }: { readonly count: number; readonly testid: string }): JSX.Element {
  const C = useC();
  return (
    <span
      data-testid={testid}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 6px',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1,
        color: C.muted,
      }}
    >
      also on {count.toLocaleString('en-US')} other Sill {count === 1 ? 'site' : 'sites'}
    </span>
  );
}

function CampaignsCard({
  state,
  top,
  onOpenAll,
}: {
  readonly state: RemoteState<ThreatCampaignsListResponse>;
  readonly top: readonly ThreatCampaignSummary[];
  readonly onOpenAll: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
          Campaigns · coordinated groups
        </div>
        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Couldn’t load campaigns. Try again in a moment.
          </div>
        ) : top.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            No coordinated groups in this window.
          </div>
        ) : (
          <>
            {top.map((c) => (
              <div
                key={c.campaign_id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>
                    {c.sender_count.toLocaleString('en-US')} addresses on {c.as_org ?? 'one network'}
                  </div>
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                    {c.dominant_class_label} · {c.any_succeeded ? 'one got through' : 'nothing got through'}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={onOpenAll}
              style={{
                font: `400 13px ${sansFont}`,
                padding: '4px 0',
                color: C.limeFg,
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              Open Attackers →
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

function WhereFromCard({
  senderCount,
  totalCount,
  asnAgg,
  countries,
}: {
  readonly senderCount: number;
  readonly totalCount: number;
  readonly asnAgg: ReadonlyArray<{ org: string; share: number }>;
  readonly countries: readonly string[];
}): JSX.Element {
  const C = useC();
  // Honest footnote — say "most recent N senders" when the full
  // total is larger than the page we loaded.
  const footnote = totalCount > senderCount
    ? `Across the ${senderCount.toLocaleString('en-US')} most recent ${senderCount === 1 ? 'sender' : 'senders'}.`
    : `Based on the ${senderCount.toLocaleString('en-US')} ${senderCount === 1 ? 'sender' : 'senders'} listed on this page.`;
  return (
    <Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
          Where from
        </div>
        {senderCount === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            No senders in this window.
          </div>
        ) : (
          <>
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body, lineHeight: 1.7 }}>
              {asnAgg.map((a, i) => (
                <span key={a.org}>
                  {i > 0 ? ' · ' : ''}
                  {a.org} {Math.round(a.share * 100)}%
                </span>
              ))}
            </div>
            {countries.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontFamily: sansFont, fontSize: 13, color: C.body }}>
                {countries.map((code) => (
                  <span key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <CountryFlag code={code} withName size={16} />
                  </span>
                ))}
              </div>
            ) : null}
            <div data-testid="where-from-footnote" style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
              {footnote}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function WhatCard({
  state,
  classAgg,
  senderCount,
  totalCount,
}: {
  readonly state: RemoteState<ThreatSendersListResponse>;
  readonly classAgg: ReadonlyArray<{ label: string; count: number }>;
  readonly senderCount: number;
  readonly totalCount: number;
}): JSX.Element {
  const C = useC();
  const max = classAgg.reduce((n, c) => (c.count > n ? c.count : n), 1);
  // Founder review: the micro-bars look weighty on only a few rows. If
  // the loaded page is fewer than 5 senders, degrade to a one-line list
  // (label · plain line · count). Keep the bars for a meaningful page.
  const asBars = senderCount >= 5;
  // Honest footnote: say so when we only cover the senders LOADED.
  const footnote = totalCount > senderCount
    ? `Across the ${senderCount.toLocaleString('en-US')} most recent ${senderCount === 1 ? 'sender' : 'senders'}.`
    : `How many of the ${senderCount.toLocaleString('en-US')} ${senderCount === 1 ? 'sender' : 'senders'} tried each.`;
  return (
    <Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
          What they looked for
        </div>
        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Couldn’t load the breakdown. Try again in a moment.
          </div>
        ) : classAgg.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Nothing to show for this period.
          </div>
        ) : asBars ? (
          <div data-testid="what-they-looked-for-bars" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {classAgg.map((c) => {
              const plain = plainClassLine(c.label);
              return (
                <div
                  key={c.label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 96px 88px',
                    gap: 10,
                    alignItems: 'center',
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.body,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div>{c.label}</div>
                    {plain !== null ? (
                      <div
                        data-testid={`what-plain-${c.label}`}
                        style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}
                      >
                        {plain}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ height: 6, background: C.surface2, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: 6, background: C.text, borderRadius: 2, width: `${Math.max(4, (c.count / max) * 100)}%` }} />
                  </div>
                  <span style={{ textAlign: 'right', fontFamily: monoFont, fontSize: 12 }}>
                    {c.count.toLocaleString('en-US')} {c.count === 1 ? 'sender' : 'senders'}
                  </span>
                </div>
              );
            })}
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>{footnote}</div>
          </div>
        ) : (
          <div data-testid="what-they-looked-for-list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {classAgg.map((c) => {
              const plain = plainClassLine(c.label);
              return (
                <div
                  key={c.label}
                  style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}
                >
                  <span style={{ fontWeight: 500 }}>{c.label}</span>
                  <span style={{ color: C.muted }}>
                    {' · '}{c.count.toLocaleString('en-US')} {c.count === 1 ? 'sender' : 'senders'}
                    {plain !== null ? ` · ${plain}` : ''}
                  </span>
                </div>
              );
            })}
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2 }}>{footnote}</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function TopPathsCard({
  state,
  onShowAll,
}: {
  readonly state: RemoteState<ProtectedPathsResponse>;
  readonly onShowAll: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
          Most-requested attack paths
        </div>
        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Couldn’t load the path list. Try again in a moment.
          </div>
        ) : state.data.top_paths.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Nothing to show for this period.
          </div>
        ) : (
          <>
            <div style={{ fontFamily: monoFont, fontSize: 12, lineHeight: 1.9, color: C.body }}>
              {state.data.top_paths.slice(0, 5).map((p) => (
                <div key={p.path}>
                  {p.path} · {p.count.toLocaleString('en-US')}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={onShowAll}
              style={{
                font: `400 13px ${sansFont}`,
                padding: '4px 0',
                color: C.limeFg,
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              Show every path →
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

function NextActions({
  actions,
  loading,
}: {
  readonly actions: ReadonlyArray<{
    key: string;
    title: string;
    hint: string;
    onClick: () => void;
    primary: boolean;
    cta: string;
  }>;
  readonly loading: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div
      data-testid="security-next-actions"
      style={{
        background: C.surface,
        border: `1px solid ${C.text}`,
        borderRadius: 3,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
        What to do next
      </div>
      {loading ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
      ) : actions.length === 0 ? (
        <div data-testid="security-next-actions-empty" style={{ fontFamily: sansFont, fontSize: 14, color: C.body }}>
          Nothing to do right now.
        </div>
      ) : (
        actions.map((a, i) => (
          <div
            key={a.key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              paddingBottom: i < actions.length - 1 ? 10 : 0,
              borderBottom: i < actions.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>
                {i + 1}. {a.title}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2 }}>
                {a.hint}
              </div>
            </div>
            <Button
              type="button"
              variant={a.primary ? 'primary' : 'secondary'}
              onClick={a.onClick}
              data-testid={`security-next-action-${a.key}`}
            >
              {a.cta}
            </Button>
          </div>
        ))
      )}
    </div>
  );
}
